import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { MessageSquare, Send, ArrowLeft, Wrench, Building2, RefreshCw, CheckCheck } from 'lucide-react';

const B2BChatPanel = ({ api, repairs }) => {
  const { t } = useLanguage();
  const [conversations, setConversations] = useState([]);
  const [activeConvo, setActiveConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  const fetchConversations = useCallback(async () => {
    try { const res = await api.get('/b2b/chat/conversations'); setConversations(res.data); } catch (err) { console.error(err); }
  }, [api]);

  useEffect(() => {
    const init = async () => { setLoading(true); await fetchConversations(); setLoading(false); };
    init();
  }, [fetchConversations]);

  const openConversation = async (convo) => {
    setActiveConvo(convo);
    try {
      const res = await api.get(`/b2b/chat/conversations/${convo.id}/messages`);
      setMessages(res.data);
      await api.post(`/b2b/chat/conversations/${convo.id}/mark-read`);
      fetchConversations();
    } catch (err) { console.error(err); }
  };

  useEffect(() => {
    if (!activeConvo) return;
    const poll = async () => { try { const res = await api.get(`/b2b/chat/conversations/${activeConvo.id}/messages`); setMessages(res.data); } catch {} };
    pollRef.current = setInterval(poll, 5000);
    return () => clearInterval(pollRef.current);
  }, [activeConvo, api]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const startConversation = async (type, repairRef) => {
    try {
      const res = await api.post('/b2b/chat/conversations', { related_type: type, related_id: repairRef || null });
      await fetchConversations();
      openConversation(res.data);
    } catch (err) { alert('Erreur lors de la creation de la conversation'); }
  };

  const sendMessage = async () => {
    if (!input.trim() || !activeConvo) return;
    setSending(true);
    try {
      const res = await api.post(`/b2b/chat/conversations/${activeConvo.id}/messages`, { content: input.trim() });
      setMessages(prev => [...prev, res.data]);
      setInput('');
    } catch (err) { alert('Erreur envoi message'); }
    finally { setSending(false); }
  };

  if (activeConvo) {
    return (
      <div className="flex flex-col h-[calc(100vh-140px)] rounded-2xl border border-white/[0.06] overflow-hidden shadow-2xl" style={{background: 'linear-gradient(145deg, rgba(15,15,22,0.98), rgba(10,10,16,0.99))'}} data-testid="b2b-chat-active">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-3 border-b border-white/[0.06] shrink-0" style={{background: 'rgba(16,16,24,0.95)'}}>
          <button onClick={() => { setActiveConvo(null); fetchConversations(); }} className="w-9 h-9 rounded-xl bg-white/[0.06] hover:bg-white/10 flex items-center justify-center text-gray-400 hover:text-white transition-all" data-testid="b2b-chat-back">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-md" style={{ background: 'var(--theme-primary)' }}>
            {activeConvo.related_type === 'b2b_repair' ? <Wrench className="w-5 h-5" /> : <Building2 className="w-5 h-5" />}
          </div>
          <div>
            <p className="text-white font-bold text-sm">{activeConvo.title}</p>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
              <p className="text-[11px] text-gray-500">{activeConvo.related_type === 'b2b_repair' ? 'Discussion reparation' : 'Discussion generale'}</p>
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-1" style={{background: 'linear-gradient(180deg, rgba(10,10,16,0.8) 0%, rgba(14,14,22,0.6) 100%)'}}>
          {messages.length === 0 && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-white/[0.03] flex items-center justify-center mx-auto mb-3">
                  <MessageSquare className="w-7 h-7 text-gray-700" />
                </div>
                <p className="text-sm text-gray-500">Aucun message. Commencez la conversation !</p>
              </div>
            </div>
          )}
          {messages.map(msg => {
            const isMe = msg.sender_role === 'b2b';
            return (
              <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'} mb-1`}>
                <div className="max-w-[75%]">
                  {!isMe && <span className="text-[10px] text-gray-500 font-medium ml-1 mb-0.5 block">{msg.sender_name}</span>}
                  <div className={`px-3.5 py-2.5 text-[13.5px] leading-relaxed break-words ${
                    isMe
                      ? 'rounded-2xl rounded-br-md text-white shadow-lg'
                      : 'bg-white/[0.07] rounded-2xl rounded-bl-md text-gray-200 border border-white/[0.06]'
                  }`} style={isMe ? { background: 'var(--theme-primary)' } : {}}>
                    {msg.content}
                    <div className={`flex items-center gap-1 mt-1 ${isMe ? 'justify-end' : ''}`}>
                      <span className={`text-[10px] ${isMe ? 'text-white/60' : 'text-gray-600'}`}>
                        {new Date(msg.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      {isMe && <CheckCheck className="w-3.5 h-3.5 text-white/50" />}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="px-4 sm:px-5 py-3 border-t border-white/[0.06] shrink-0" style={{background: 'rgba(16,16,24,0.95)'}}>
          <div className="flex items-end gap-3">
            <input type="text" value={input} onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
              className="flex-1 px-4 py-3 bg-white/[0.05] border border-white/[0.08] rounded-2xl text-white text-sm placeholder-gray-600 focus:border-white/20 outline-none transition-all"
              placeholder="Votre message..." data-testid="b2b-chat-input" />
            <button onClick={sendMessage} disabled={sending || !input.trim()}
              className="w-11 h-11 rounded-2xl text-white flex items-center justify-center transition-all disabled:opacity-30 shadow-lg shrink-0"
              style={{ background: 'var(--theme-primary)', boxShadow: '0 4px 12px rgba(var(--theme-primary-rgb), 0.3)' }}
              data-testid="b2b-chat-send">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="b2b-chat-list">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl flex items-center justify-center shadow-lg" style={{ background: 'var(--theme-primary)', boxShadow: '0 4px 12px rgba(var(--theme-primary-rgb), 0.3)' }}>
            <MessageSquare className="w-5 h-5 text-white" />
          </div>
          <h1 className="text-xl font-bold text-white">Messages</h1>
        </div>
        <button onClick={() => startConversation('b2b_general', null)}
          className="px-4 py-2.5 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white rounded-xl text-sm font-semibold transition-all flex items-center gap-2 shadow-lg shadow-purple-500/20"
          data-testid="b2b-chat-new-general">
          <MessageSquare className="w-4 h-4" /> Nouvelle discussion
        </button>
      </div>

      {/* Quick start per-repair chats */}
      {repairs && repairs.length > 0 && (
        <div>
          <p className="text-xs text-gray-500 mb-2 font-medium">Discuter d'une reparation :</p>
          <div className="flex gap-2 flex-wrap">
            {repairs.filter(r => r.status !== 'Livre').slice(0, 5).map(r => (
              <button key={r.ref} onClick={() => startConversation('b2b_repair', r.ref)}
                className="px-3 py-2 bg-white/[0.04] border border-white/[0.08] rounded-xl text-xs text-gray-300 hover:bg-purple-500/10 hover:border-purple-500/20 transition-all flex items-center gap-1.5"
                data-testid={`b2b-chat-start-${r.ref}`}>
                <Wrench className="w-3 h-3 text-purple-400" /> {r.ref}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Conversations list */}
      {loading ? (
        <div className="text-center py-10"><RefreshCw className="w-6 h-6 text-purple-400 animate-spin mx-auto" /></div>
      ) : conversations.length === 0 ? (
        <div className="text-center py-14 bg-white/[0.02] border border-white/[0.06] rounded-2xl">
          <div className="w-16 h-16 rounded-full bg-white/[0.03] flex items-center justify-center mx-auto mb-4">
            <MessageSquare className="w-7 h-7 text-gray-700" />
          </div>
          <p className="text-gray-400 text-sm">Aucune conversation</p>
          <p className="text-gray-600 text-xs mt-1">Demarrez une discussion avec le staff</p>
        </div>
      ) : (
        <div className="space-y-2">
          {conversations.map(convo => (
            <button key={convo.id} onClick={() => openConversation(convo)}
              className="w-full flex items-center gap-3 p-4 bg-white/[0.03] border border-white/[0.06] rounded-2xl hover:bg-white/[0.06] hover:border-white/10 transition-all text-left"
              data-testid={`b2b-convo-${convo.id}`}>
              <div className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 shadow-md" style={{ background: 'var(--theme-primary)' }}>
                {convo.related_type === 'b2b_repair' ? <Wrench className="w-5 h-5 text-white" /> : <Building2 className="w-5 h-5 text-white" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-white font-semibold text-sm truncate">{convo.title}</p>
                  {convo.unread_count > 0 && (
                    <span className="text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-bold flex-shrink-0 shadow-lg" style={{ background: 'var(--theme-primary)', boxShadow: '0 2px 8px rgba(var(--theme-primary-rgb), 0.4)' }}>{convo.unread_count}</span>
                  )}
                </div>
                {convo.last_message && (
                  <p className="text-[11px] text-gray-500 truncate mt-0.5">{convo.last_message.sender_name}: {convo.last_message.content}</p>
                )}
              </div>
              <span className="text-[10px] text-gray-600 flex-shrink-0">
                {convo.last_message ? new Date(convo.last_message.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) : ''}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default B2BChatPanel;
